/**
 * Emergent Marketplace — Phase 2
 * VendorProducts.jsx
 * Architectural Layer: Presentation → Vendor Dashboard
 *
 * Full product management: list, create, edit, publish, archive.
 * Handles all 4 product types with type-specific field sets.
 * Design: Editorial warmth — linen white, forest ink, copper accents.
 * Font: Spectral (display) + Nunito Sans (UI)
 */

import { useState, useEffect, useRef } from "react";
import {
  Plus, Package, Download,
  Edit2, Archive, Eye, EyeOff, ChevronDown,
  X, Check, AlertCircle, Upload, DollarSign,
  Tag, Layers, ToggleLeft, ToggleRight, Loader,
  Globe, FileSpreadsheet, ExternalLink, Image as ImageIcon, Trash2, AlertTriangle
} from "lucide-react";
import { apiFetch } from "./api";
import { ProductStock } from "../../components/marketplace/ProductStock";
import { VendorCollectionCovers } from "../../components/marketplace/VendorCollectionCovers";
import ProductImportModal from "./ProductImportModal";

// Sales-tax categories that make a product a consumable — kept in step with
// routes/tax/codes.py and returns_policy.CONSUMABLE_TAX_TYPES.
const CONSUMABLE_TAX_TYPES = ["supplements", "herbs_tea", "food_drink", "cosmetics"];

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;0,600;1,400&family=Nunito+Sans:wght@300;400;600;700&display=swap');

  .vp-root {
    --linen: #faf7f2;
    --linen-dark: #f0ebe0;
    --forest: #1e3a2a;
    --forest-mid: #2d5a3d;
    --copper: #b5622a;
    --copper-light: #d4895a;
    --copper-bg: #fdf5ee;
    --copper-border: #e8c4a8;
    --ink: #1a1a1a;
    --ink-mid: #4a4a4a;
    --ink-light: #8a8a8a;
    --border: #e2dbd0;
    --surface: #ffffff;
    --green: #2d6b4a;
    --green-bg: #eef5f1;
    --red: #b02020;
    --red-bg: #fdf0f0;
    font-family: 'Nunito Sans', sans-serif;
    background: var(--linen);
    color: var(--ink);
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* Header */
  .vp-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 20px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 30;
    flex-wrap: wrap;
    gap: 12px;
  }

  .vp-header h1 {
    font-family: 'Spectral', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--forest);
    margin: 0 0 2px;
  }

  .vp-header p { font-size: 13px; color: var(--ink-light); margin: 0; }

  .vp-btn-new {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: var(--forest);
    border: none;
    border-radius: 10px;
    color: white;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s;
  }

  .vp-btn-new:hover { background: var(--forest-mid); }

  @media (max-width: 768px) {
    .vp-header {
      padding: 14px 16px;
      flex-direction: column;
      align-items: stretch;
    }
    .vp-header h1 { font-size: 18px; }
    .vp-btn-new {
      padding: 8px 12px;
      font-size: 12px;
      gap: 5px;
      flex: 1;
      justify-content: center;
      min-width: 0;
    }
  }

  /* Type filter tabs */
  .vp-type-tabs {
    display: flex;
    gap: 4px;
    padding: 16px 28px 12px;
    overflow-x: auto;
  }

  .vp-type-tab {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 7px 16px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: var(--surface);
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-light);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .vp-type-tab.active {
    background: var(--forest);
    border-color: var(--forest);
    color: white;
  }

  .vp-type-tab-count {
    background: rgba(255,255,255,0.2);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
  }

  .vp-type-tab:not(.active) .vp-type-tab-count {
    background: var(--linen-dark);
    color: var(--ink-light);
  }

  /* Product grid */
  .vp-grid {
    padding: 8px 28px 40px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  .vp-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .vp-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.07); }

  .vp-card-img {
    height: 140px;
    background: linear-gradient(135deg, var(--linen-dark), #e8e0d0);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ink-light);
    position: relative;
  }

  .vp-card-type-badge {
    position: absolute;
    top: 10px; left: 10px;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .vp-badge-physical  { background: #e8f0fe; color: #1a5fa8; }
  .vp-badge-digital   { background: #e8f5e9; color: #1a7a4a; }
  .vp-badge-course    { background: var(--copper-bg); color: var(--copper); }
  .vp-badge-session   { background: #f3e8fd; color: #6a1fa8; }

  .vp-card-status-dot {
    position: absolute;
    top: 10px; right: 10px;
    width: 8px; height: 8px;
    border-radius: 50%;
  }

  .vp-card-status-dot.active   { background: #2d6b4a; box-shadow: 0 0 0 3px rgba(45,107,74,0.2); }
  .vp-card-status-dot.draft    { background: #b0a090; }
  .vp-card-status-dot.archived { background: #d0c8c0; }

  .vp-card-body { padding: 16px; }

  .vp-card-name {
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 6px;
    line-height: 1.3;
  }

  .vp-card-meta {
    display: flex;
    gap: 12px;
    align-items: center;
    font-size: 13px;
    color: var(--ink-light);
    margin-bottom: 14px;
  }

  .vp-card-price { font-weight: 700; color: var(--forest); }

  /* Stock on the card — read and corrected in the same place */
  .vp-stock-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 12px;
    font-size: 12px;
  }

  .vp-stock-label {
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 10px;
    font-weight: 700;
    color: var(--ink-light);
  }

  .vp-stock-value {
    min-width: 34px;
    padding: 2px 8px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: var(--linen-dark);
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
    cursor: pointer;
    transition: border-color 140ms ease, background-color 140ms ease;
  }
  .vp-stock-value:hover { border-color: var(--ink-mid); }
  .vp-stock-value.low  { background: #fdf0e2; color: #a15c12; }
  .vp-stock-value.zero { background: var(--red-bg); color: var(--red); }

  .vp-stock-input {
    width: 58px;
    padding: 2px 6px;
    border: 1px solid var(--forest);
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    background: var(--surface);
    color: var(--ink);
  }

  .vp-stock-expand {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px 2px 2px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: none;
    font-family: inherit;
    font-size: 12px;
    color: var(--ink-mid);
    cursor: pointer;
    transition: background-color 140ms ease;
  }
  .vp-stock-expand:hover { background: var(--linen-dark); }
  .vp-stock-expand strong { font-size: 13px; color: var(--ink); }
  .vp-stock-expand strong.low { color: #a15c12; }

  .vp-stock-types {
    margin: -4px 0 12px;
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--linen);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .vp-stock-type {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  .vp-stock-type-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--ink-mid);
  }

  .vp-stock-sku {
    font-size: 10px;
    color: var(--ink-light);
    font-variant-numeric: tabular-nums;
  }

  .vp-stock-untracked {
    border: 1px dashed var(--border);
    border-radius: 6px;
    background: none;
    padding: 2px 8px;
    font-family: inherit;
    font-size: 11px;
    color: var(--ink-light);
    cursor: pointer;
    transition: border-color 140ms ease, color 140ms ease;
  }
  .vp-stock-untracked:hover { border-color: var(--forest); color: var(--forest); }

  .vp-stock-mto {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--copper);
    font-weight: 600;
  }

  .vp-card-actions {
    display: flex;
    gap: 8px;
  }

  .vp-card-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border-radius: 7px;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s;
    border: 1px solid var(--border);
    background: var(--linen);
    color: var(--ink-mid);
  }

  .vp-card-btn:hover { border-color: var(--ink-mid); background: var(--linen-dark); }

  .vp-card-btn.publish { border-color: var(--green); color: var(--green); background: var(--green-bg); }
  .vp-card-btn.unpublish { border-color: var(--border); color: var(--ink-light); }
  .vp-card-btn.archive { border-color: var(--red); color: var(--red); background: var(--red-bg); }

  /* Empty state */
  .vp-empty {
    grid-column: 1 / -1;
    text-align: center;
    padding: 60px 20px;
  }

  .vp-empty-icon {
    width: 64px; height: 64px;
    border-radius: 50%;
    background: var(--linen-dark);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: var(--ink-light);
  }

  /* ── DRAWER ─────────────────────────────────────────────────────────────── */
  .vp-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.35);
    z-index: 100;
    animation: vp-fadein 0.2s ease;
  }

  @keyframes vp-fadein { from { opacity: 0; } to { opacity: 1; } }

  .vp-drawer {
    position: fixed;
    top: 0; right: 0;
    width: 100%;
    max-width: 520px;
    height: 100vh;
    background: var(--surface);
    z-index: 101;
    display: flex;
    flex-direction: column;
    animation: vp-slidein 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -8px 0 32px rgba(0,0,0,0.12);
  }

  @keyframes vp-slidein {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }

  .vp-drawer-header {
    padding: 20px 24px;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  }

  .vp-drawer-title {
    font-family: 'Spectral', serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--forest);
    margin: 0;
  }

  .vp-drawer-close {
    width: 32px; height: 32px;
    border-radius: 50%;
    border: 1px solid var(--border);
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--ink-mid);
    transition: background 0.15s;
  }

  .vp-drawer-close:hover { background: var(--linen-dark); }

  .vp-drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  .vp-drawer-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 10px;
    flex-shrink: 0;
  }

  /* Type selector */
  .vp-type-selector {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 24px;
  }

  .vp-type-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s;
    background: var(--surface);
  }

  .vp-type-option:hover { border-color: var(--forest-mid); }
  .vp-type-option.selected { border-color: var(--forest); background: rgba(30,58,42,0.04); }

  .vp-type-option-icon {
    width: 36px; height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .vp-type-option-label { font-size: 13px; font-weight: 700; color: var(--ink); }
  .vp-type-option-desc  { font-size: 11px; color: var(--ink-light); margin-top: 1px; }

  /* Form fields */
  .vp-field { margin-bottom: 20px; }

  .vp-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin-bottom: 7px;
  }

  .vp-input, .vp-textarea, .vp-select {
    width: 100%;
    background: var(--linen);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    padding: 11px 14px;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    color: var(--ink);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }

  .vp-input:focus, .vp-textarea:focus, .vp-select:focus {
    border-color: var(--forest);
    box-shadow: 0 0 0 3px rgba(30,58,42,0.08);
    background: var(--surface);
  }

  .vp-textarea { min-height: 90px; resize: vertical; line-height: 1.6; }

  /* Narrow number boxes: 14px of side padding left barely two digits visible, so a type's
     54 x 12 x 24 read as 5 x 1 x 2 (vendor, Oct 2026). Tighter padding, no spinner arrows. */
  .vp-input-sm {
    padding: 10px 6px;
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
  }
  .vp-input-sm::-webkit-outer-spin-button,
  .vp-input-sm::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .vp-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238a8a8a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    padding-right: 38px;
    cursor: pointer;
  }

  .vp-row { display: flex; gap: 12px; }
  .vp-row .vp-field { flex: 1; }

  /* On a phone, weight beside three dimension boxes leaves each too narrow to read */
  @media (max-width: 520px) {
    .vp-row { flex-wrap: wrap; }
    .vp-row .vp-field { flex: 1 1 100%; }
  }

  .vp-section-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 24px 0 16px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-light);
  }

  .vp-section-divider::before, .vp-section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }

  /* Toggle */
  .vp-toggle-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid var(--linen-dark);
  }

  .vp-toggle-label { font-size: 14px; color: var(--ink); font-weight: 600; }
  .vp-toggle-desc  { font-size: 12px; color: var(--ink-light); margin-top: 2px; }

  .vp-toggle {
    width: 44px; height: 24px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    padding: 2px;
    transition: background 0.2s;
    flex-shrink: 0;
  }

  .vp-toggle.on  { background: var(--forest); }
  .vp-toggle.off { background: var(--border); }

  .vp-toggle-knob {
    width: 20px; height: 20px;
    border-radius: 50%;
    background: white;
    transition: transform 0.2s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }

  .vp-toggle.on .vp-toggle-knob  { transform: translateX(20px); }
  .vp-toggle.off .vp-toggle-knob { transform: translateX(0); }

  /* Tags input */
  .vp-tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px;
    background: var(--linen);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    min-height: 44px;
    cursor: text;
  }

  .vp-tag-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px 3px 10px;
    background: var(--forest);
    color: white;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
  }

  .vp-tag-remove {
    background: none;
    border: none;
    color: rgba(255,255,255,0.7);
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
  }

  .vp-tags-input {
    background: none;
    border: none;
    outline: none;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    color: var(--ink);
    min-width: 100px;
    flex: 1;
  }

  /* Buttons */
  .vp-btn-primary {
    flex: 1;
    padding: 12px;
    border-radius: 10px;
    background: var(--forest);
    border: none;
    color: white;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.15s;
  }

  .vp-btn-primary:hover { background: var(--forest-mid); }
  .vp-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .vp-btn-secondary {
    padding: 12px 20px;
    border-radius: 10px;
    border: 1.5px solid var(--border);
    background: none;
    color: var(--ink-mid);
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .vp-btn-secondary:hover { border-color: var(--ink-mid); }

  .vp-btn-publish-draft {
    padding: 12px 20px;
    border-radius: 10px;
    border: 1.5px solid var(--green);
    background: var(--green-bg);
    color: var(--green);
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .vp-btn-publish-draft:hover { opacity: 0.85; }

  .vp-saving {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--ink-light);
    animation: spin 1s linear infinite;
  }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  physical: { label: "Physical",  icon: Package,  color: "#1a5fa8", bg: "#e8f0fe" },
  digital:  { label: "Digital",   icon: Download, color: "#1a7a4a", bg: "#e8f5e9" },
};

// ─── Subcomponents ────────────────────────────────────────────────────────────

function TagsInput({ tags, onChange }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);

  const addTag = (val) => {
    const tag = val.trim().toLowerCase().replace(/\s+/g, "-");
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInput("");
  };

  return (
    <div
      className="vp-tags-container"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map(t => (
        <span key={t} className="vp-tag-chip">
          {t}
          <button className="vp-tag-remove" onClick={() => onChange(tags.filter(x => x !== t))}>
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="vp-tags-input"
        placeholder={tags.length === 0 ? "Add tags (press Enter)" : ""}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(input); }
          if (e.key === "Backspace" && !input && tags.length) onChange(tags.slice(0, -1));
        }}
      />
    </div>
  );
}

function Toggle({ on, onToggle, testId }) {
  return (
    <button className={`vp-toggle ${on ? "on" : "off"}`} onClick={onToggle} data-testid={testId}>
      <div className="vp-toggle-knob" />
    </button>
  );
}

// ─── Category Searchable Input (like Workshop Builder) ─────────────────────

function CategoryInput({ value, onChange, categories, onCreateCategory }) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);

  const filtered = categories.filter(c =>
    c.name.toLowerCase().includes((input || "").toLowerCase())
  );

  const display = input || (!open ? value : "");

  const handleSelect = (name) => {
    onChange(name);
    setInput("");
    setOpen(false);
  };

  const handleCreate = async () => {
    if (!input.trim()) return;
    setCreating(true);
    try {
      const cat = await onCreateCategory(input.trim());
      if (cat?.name) handleSelect(cat.name);
    } finally {
      setCreating(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        className="vp-input"
        value={display}
        onChange={e => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); if (value && !input) setInput(""); }}
        onKeyDown={e => {
          if (e.key === "Enter" && input.trim() && !filtered.some(c => c.name.toLowerCase() === input.toLowerCase())) {
            e.preventDefault();
            handleCreate();
          }
        }}
        placeholder="Search or create category..."
        data-testid="product-category-input"
      />
      {open && (
        <div style={{
          position: "absolute", zIndex: 60, width: "100%", marginTop: 4,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          maxHeight: 200, overflowY: "auto", WebkitOverflowScrolling: "touch",
        }} data-testid="product-category-dropdown">
          {filtered.map(c => (
            <div
              key={c.id || c.name}
              style={{
                padding: "8px 14px", cursor: "pointer", fontSize: 13,
                background: value === c.name ? "var(--linen-dark)" : "transparent",
              }}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(c.name); }}
              onTouchEnd={(e) => { e.preventDefault(); handleSelect(c.name); }}
            >
              {c.name}
            </div>
          ))}
          {!filtered.length && !input.trim() && (
            <div style={{ padding: "8px 14px", fontSize: 12, color: "var(--ink-light)" }}>
              No categories yet. Type to create one.
            </div>
          )}
          {input.trim() && !filtered.some(c => c.name.toLowerCase() === input.toLowerCase()) && (
            <div
              style={{
                padding: "8px 14px", cursor: "pointer", fontSize: 13,
                background: "rgba(26,95,68,0.08)", borderTop: "1px solid var(--border)",
                color: "var(--forest)", fontWeight: 600,
              }}
              onMouseDown={(e) => { e.preventDefault(); handleCreate(); }}
              onTouchEnd={(e) => { e.preventDefault(); handleCreate(); }}
            >
              {creating ? "Creating..." : `+ Create "${input.trim()}"`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Variants (Types) Editor ───────────────────────────────────────────────

function VariantsEditor({ variants, onChange, basePrice, onVariantImageUpload,
                         isPhysical = false, inheritWeight = "", inheritDims = {} }) {
  const addVariant = () => {
    onChange([...variants, { name: "", price_adjustment: 0, stock: 0, sku: "",
                             weight_oz: "", dimensions: { l: "", w: "", h: "" } }]);
  };

  const update = (idx, field, val) => {
    onChange(variants.map((v, i) => i === idx ? { ...v, [field]: val } : v));
  };

  const remove = (idx) => {
    onChange(variants.filter((_, i) => i !== idx));
  };

  const bp = parseFloat(basePrice) || 0;

  return (
    <div>
      {variants.map((v, i) => {
        const adj = parseFloat(v.price_adjustment) || 0;
        const finalPrice = Math.max(0, bp + adj).toFixed(2);
        return (
          <div key={i} style={{
            background: "var(--linen-dark)", border: "1px solid var(--border)",
            borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              {/* Type image */}
              <label style={{
                width: 40, height: 40, borderRadius: 6, flexShrink: 0, cursor: "pointer",
                overflow: "hidden", border: "1px solid var(--border)",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: v.image_url ? "transparent" : "var(--linen)",
              }} data-testid={`variant-image-${i}`}>
                {v.image_url ? (
                  <img src={v.image_url} alt={v.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <ImageIcon size={16} color="var(--ink-light)" />
                )}
                <input type="file" accept="image/*" onChange={(e) => onVariantImageUpload(e, i)} style={{ display: "none" }} />
              </label>
              <input
                className="vp-input"
                placeholder="Type name (e.g. Heart Chakra)"
                value={v.name}
                onChange={e => update(i, "name", e.target.value)}
                style={{ flex: 1, minWidth: 140 }}
                data-testid={`variant-name-${i}`}
              />
              <button
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-light)", padding: 4 }}
                onClick={() => remove(i)}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "var(--ink-light)", whiteSpace: "nowrap" }}>Price adj.</span>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <button
                    className="vp-adj-btn"
                    style={{
                      width: 24, height: 24, borderRadius: 6, border: "1px solid var(--border)",
                      background: adj < 0 ? "var(--forest)" : "var(--linen)",
                      color: adj < 0 ? "white" : "var(--ink)", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700,
                    }}
                    onClick={() => {
                      const curr = Math.abs(adj);
                      update(i, "price_adjustment", curr > 0 ? -curr : 0);
                    }}
                    data-testid={`variant-minus-${i}`}
                  >-</button>
                  <input
                    className="vp-input vp-input-sm"
                    type="number"
                    step="0.01"
                    min="0"
                    value={Math.abs(adj) || ""}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      update(i, "price_adjustment", adj < 0 ? -val : val);
                    }}
                    style={{ width: 76, textAlign: "center" }}
                    data-testid={`variant-adj-${i}`}
                  />
                  <button
                    className="vp-adj-btn"
                    style={{
                      width: 24, height: 24, borderRadius: 6, border: "1px solid var(--border)",
                      background: adj >= 0 && adj !== 0 ? "var(--forest)" : "var(--linen)",
                      color: adj >= 0 && adj !== 0 ? "white" : "var(--ink)", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700,
                    }}
                    onClick={() => {
                      const curr = Math.abs(adj);
                      update(i, "price_adjustment", curr > 0 ? curr : 0);
                    }}
                    data-testid={`variant-plus-${i}`}
                  >+</button>
                </div>
                <span style={{ fontSize: 12, color: "var(--forest)", fontWeight: 600, whiteSpace: "nowrap" }}>
                  = ${finalPrice}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "var(--ink-light)", whiteSpace: "nowrap" }}>Stock</span>
                <input
                  className="vp-input vp-input-sm"
                  type="number"
                  min="0"
                  value={v.stock || ""}
                  onChange={e => update(i, "stock", parseInt(e.target.value) || 0)}
                  style={{ width: 70, textAlign: "center" }}
                  data-testid={`variant-stock-${i}`}
                />
              </div>
            </div>

            {isPhysical && (
              <div style={{
                display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--border)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 12, color: "var(--ink-light)", whiteSpace: "nowrap" }}>Weight (oz)</span>
                  <input
                    className="vp-input vp-input-sm"
                    type="number" min="0" step="0.1"
                    placeholder={inheritWeight ? String(inheritWeight) : "—"}
                    value={v.weight_oz ?? ""}
                    onChange={e => update(i, "weight_oz", e.target.value)}
                    style={{ width: 76, textAlign: "center" }}
                    data-testid={`variant-weight-${i}`}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 12, color: "var(--ink-light)", whiteSpace: "nowrap" }}>L × W × H (in)</span>
                  {["l", "w", "h"].map(dim => (
                    <input
                      key={dim}
                      className="vp-input vp-input-sm"
                      type="number" min="0" step="0.1"
                      placeholder={inheritDims?.[dim] ? String(inheritDims[dim]) : dim.toUpperCase()}
                      value={v.dimensions?.[dim] ?? ""}
                      onChange={e => update(i, "dimensions", { ...(v.dimensions || {}), [dim]: e.target.value })}
                      style={{ width: 64, textAlign: "center" }}
                      data-testid={`variant-dim-${dim}-${i}`}
                    />
                  ))}
                </div>
                <span style={{ fontSize: 11, color: "var(--ink-light)" }}>
                  Blank uses the product default
                </span>
              </div>
            )}
          </div>
        );
      })}
      <button
        className="vp-btn-outline"
        onClick={addVariant}
        data-testid="add-variant-btn"
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 14px", border: "1px dashed var(--border)",
          borderRadius: 8, background: "none", cursor: "pointer",
          color: "var(--forest)", fontWeight: 600, fontSize: 13,
        }}
      >
        <Plus size={14} /> Add type
      </button>
    </div>
  );
}

// ─── Product Form Drawer ──────────────────────────────────────────────────────

function ProductDrawer({ product, onClose, onSave, categories = [], onCreateCategory }) {
  const isEdit = !!product;
  const [form, setForm] = useState(product || {
    type: "physical",
    title: "",
    description: "",
    price: "",
    category: "",
    tax_type: "",
    tags: [],
    variants: [],
    status: "draft",
    returnable: true,
    inventory: { track: false, quantity: 0, sku: "" },
    shipping:  { required: false, weight_oz: "", dimensions: { l: "", w: "", h: "" } },
    digital_asset: { file_url: "", file_type: "pdf", download_limit: null },
  });

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [taxTypes, setTaxTypes] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const u = (fields) => setForm(f => ({ ...f, ...fields }));

  useEffect(() => {
    apiFetch("/marketplace/shelves")
      .then(d => setShelves(d.shelves || []))
      .catch(() => setShelves([]));
  }, []);

  const toggleShelf = (id) => {
    const current = form.shelf_ids || [];
    if (current.includes(id)) u({ shelf_ids: current.filter(s => s !== id) });
    else if (current.length < 3) u({ shelf_ids: [...current, id] });
  };

  const suggestShelves = async () => {
    setSuggesting(true);
    try {
      const d = await apiFetch("/vendor/products/suggest-shelves", {
        method: "POST",
        body: JSON.stringify({
          title: form.title, category: form.category,
          tags: form.tags || [], description: form.description,
        }),
      });
      u({ shelf_ids: (d.shelf_ids || []).slice(0, 3) });
    } catch (err) { /* leaving it blank is a valid state */ }
    finally { setSuggesting(false); }
  };

  useEffect(() => {
    apiFetch("/tax/types")
      .then(d => setTaxTypes(d.types || []))
      .catch(() => setTaxTypes([]));
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/vendor/product-image", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.file_url) {
        u({ images: [...(form.images || []), data.file_url] });
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleVariantImageUpload = async (e, variantIdx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/vendor/product-image", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.file_url) {
        const updated = [...(form.variants || [])];
        updated[variantIdx] = { ...updated[variantIdx], image_url: data.file_url };
        u({ variants: updated });
      }
    } catch (err) {
      console.error("Variant image upload failed:", err);
    }
  };

  const removeImage = (idx) => {
    u({ images: (form.images || []).filter((_, i) => i !== idx) });
  };

  const save = async (publish = false) => {
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        title: form.title,
        description: form.description,
        price: parseFloat(form.price) || 0,
        category: form.category,
        shelf_ids: form.shelf_ids || [],
        tax_type: form.tax_type || null,
        returnable: form.returnable !== false,
        consumable: form.consumable === true || CONSUMABLE_TAX_TYPES.includes(form.tax_type),
        tags: form.tags || [],
        images: form.images || [],
        variants: (form.variants || []).filter(v => v.name.trim()).map(v => {
          const vd = v.dimensions || {};
          const vHasDims = ["l", "w", "h"].some(k => vd[k] !== "" && vd[k] != null);
          return {
            name: v.name.trim(),
            price_adjustment: parseFloat(v.price_adjustment) || 0,
            stock: parseInt(v.stock, 10) || 0,
            sku: v.sku || "",
            image_url: v.image_url || "",
            weight_oz: v.weight_oz !== "" && v.weight_oz != null ? parseFloat(v.weight_oz) : null,
            dimensions: vHasDims ? {
              l: parseFloat(vd.l) || null,
              w: parseFloat(vd.w) || null,
              h: parseFloat(vd.h) || null,
            } : null,
          };
        }),
        status: publish ? "active" : (form.status || "draft"),
      };

      // Add type-specific fields
      if (form.type === "physical") {
        if (form.inventory?.track) {
          payload.inventory = {
            track: true,
            quantity: parseInt(form.inventory.quantity, 10) || 0,
            sku: form.inventory.sku || "",
          };
        }
        // Shipping always saves for a physical product. It used to be gated on
        // form.shipping.required, which no control ever set, so every weight and
        // dimension a vendor typed was silently thrown away before reaching the API.
        const dims = form.shipping?.dimensions || {};
        const hasDims = ["l", "w", "h"].some(k => dims[k] !== "" && dims[k] != null);
        payload.shipping = {
          required: true,
          weight_oz: form.shipping?.weight_oz ? parseFloat(form.shipping.weight_oz) : null,
          dimensions: hasDims ? {
            l: parseFloat(dims.l) || null,
            w: parseFloat(dims.w) || null,
            h: parseFloat(dims.h) || null,
          } : null,
        };
        payload.free_shipping = form.free_shipping === true;
        payload.made_to_order = form.made_to_order === true;
        payload.print_on_demand = form.print_on_demand === true;
        if (form.made_to_order || form.print_on_demand) {
          payload.lead_time_min = parseInt(form.lead_time_min, 10) || null;
          payload.lead_time_max = parseInt(form.lead_time_max, 10) || null;
          payload.lead_time_unit = form.lead_time_unit || "weeks";
        }
      }
      if (form.type === "digital" && form.digital_asset?.file_url) {
        payload.digital_asset = form.digital_asset;
      }

      const method = isEdit ? "PATCH" : "POST";
      const url = isEdit ? `/vendor/products/${product.id}` : `/vendor/products`;
      const data = await apiFetch(url, { method, body: JSON.stringify(payload) });
      onSave(data);
    } catch (err) {
      alert(err.message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const tc = TYPE_CONFIG[form.type];
  const namedTypes = (form.variants || []).filter(v => (v.name || "").trim());
  const hasTypes = namedTypes.length > 0;
  const typeStockTotal = namedTypes.reduce((sum, v) => sum + (parseInt(v.stock, 10) || 0), 0);

  return (
    <>
      <div className="vp-overlay" onClick={onClose} />
      <div className="vp-drawer">
        <div className="vp-drawer-header">
          <h2 className="vp-drawer-title">
            {isEdit ? "Edit product" : "Add product"}
          </h2>
          <button className="vp-drawer-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="vp-drawer-body">

          {/* Type selector — only on create */}
          {!isEdit && (
            <div className="vp-field">
              <label className="vp-label">Product type</label>
              <div className="vp-type-selector">
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={key}
                      className={`vp-type-option ${form.type === key ? "selected" : ""}`}
                      onClick={() => u({ type: key })}
                    >
                      <div className="vp-type-option-icon" style={{ background: cfg.bg }}>
                        <Icon size={18} color={cfg.color} />
                      </div>
                      <div>
                        <div className="vp-type-option-label">{cfg.label}</div>
                        <div className="vp-type-option-desc">
                          {{ physical: "Shipped item", digital: "Downloadable file" }[key]}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Core fields */}
          <div className="vp-field">
            <label className="vp-label">Product name</label>
            <input className="vp-input" placeholder="Give it a meaningful name..." value={form.title} onChange={e => u({ title: e.target.value })} />
          </div>

          <div className="vp-field">
            <label className="vp-label">Description</label>
            <textarea className="vp-textarea" placeholder="Describe what this offers and the transformation it supports..." value={form.description} onChange={e => u({ description: e.target.value })} />
          </div>

          {/* ── Product Images ── */}
          <div className="vp-field">
            <label className="vp-label">Images</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {(form.images || []).map((url, i) => (
                <div key={i} style={{ position: "relative", width: 72, height: 72 }}>
                  <img
                    src={url}
                    alt={`Product ${i + 1}`}
                    style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }}
                    data-testid={`product-image-${i}`}
                  />
                  <button
                    onClick={() => removeImage(i)}
                    style={{
                      position: "absolute", top: -6, right: -6,
                      width: 20, height: 20, borderRadius: "50%",
                      background: "#e05", color: "white", border: "none",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700,
                    }}
                  >&times;</button>
                </div>
              ))}
              <label
                style={{
                  width: 72, height: 72, borderRadius: 8,
                  border: "2px dashed var(--border)", cursor: "pointer",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 2,
                  color: "var(--ink-light)", fontSize: 11, fontWeight: 600,
                }}
                data-testid="product-image-upload-btn"
              >
                {uploading ? "..." : <><Plus size={16} /><span>Add</span></>}
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} disabled={uploading} />
              </label>
            </div>
          </div>

          <div className="vp-row">
            <div className="vp-field">
              <label className="vp-label">Price (USD)</label>
              <input className="vp-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.price} onChange={e => u({ price: e.target.value })} />
            </div>
            <div className="vp-field">
              <label className="vp-label">Category</label>
              <CategoryInput
                value={form.category || ""}
                onChange={val => u({ category: val })}
                categories={categories}
                onCreateCategory={onCreateCategory}
              />
            </div>
          </div>

          <div className="vp-field">
            <label className="vp-label">Marketplace shelves (up to 3)</label>
            <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "0 0 8px", lineHeight: 1.5 }}>
              Where shoppers find this in the marketplace. Your Category above stays yours — it
              groups this item into a collection on your own storefront.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}
              data-testid="product-shelf-picker">
              {shelves.map(s => {
                const on = (form.shelf_ids || []).includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleShelf(s.id)}
                    data-testid={`product-shelf-${s.slug}`}
                    style={{
                      padding: "6px 12px", borderRadius: 16, fontSize: 12, cursor: "pointer",
                      border: on ? "1px solid #0f766e" : "1px solid rgba(0,0,0,0.12)",
                      background: on ? "#0f766e" : "transparent",
                      color: on ? "#fff" : "var(--ink-light)",
                      transition: "background-color 0.2s, color 0.2s, border-color 0.2s",
                    }}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={suggestShelves}
              disabled={suggesting || (!form.title && !form.category)}
              data-testid="product-shelf-suggest"
              style={{
                padding: "6px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                border: "1px solid rgba(0,0,0,0.12)", background: "transparent",
                color: "var(--ink-light)",
              }}
            >
              {suggesting ? "Finding shelves…" : "Suggest shelves for me"}
            </button>
            {(form.shelf_ids || []).length === 0 && (
              <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "8px 0 0" }}>
                Leave this blank and we will shelve it for you when you save.
              </p>
            )}
          </div>

          <div className="vp-field">
            <label className="vp-label">Sales tax category</label>
            <select
              className="vp-input"
              value={form.tax_type || ""}
              onChange={e => u({ tax_type: e.target.value })}
              data-testid="product-tax-type-select"
            >
              <option value="">Automatic (based on product type)</option>
              {taxTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "6px 0 0", lineHeight: 1.5 }}>
              Leave on Automatic unless this item is taxed differently — clothing, supplements
              and digital files follow their own state rules.
            </p>
          </div>

          <div className="vp-field">
            <label className="vp-label">Tags</label>
            <TagsInput tags={form.tags || []} onChange={tags => u({ tags })} />
          </div>

          <div className="vp-toggle-row" style={{ marginTop: 4 }}>
            <div>
              <div className="vp-toggle-label">Eligible for returns</div>
              <div className="vp-toggle-desc">
                On by default. Switch it off to make this one item final sale, whatever your
                store policy says.
              </div>
            </div>
            <Toggle
              on={form.returnable !== false}
              onToggle={() => u({ returnable: form.returnable === false })}
              testId="product-returnable-toggle"
            />
          </div>

          <div className="vp-toggle-row" style={{ marginTop: 4 }}>
            <div>
              <div className="vp-toggle-label">Consumable — can&apos;t be returned once shipped</div>
              <div className="vp-toggle-desc">
                Supplements, herbs and teas, food, cosmetics and body care — anything you
                can&apos;t resell once it leaves you.
                {CONSUMABLE_TAX_TYPES.includes(form.tax_type)
                  ? " Set for you by the sales tax category below, so it can't be switched off."
                  : ' Buyers see: "This item is a consumable and can\'t be returned once shipped."'}
              </div>
            </div>
            <Toggle
              on={form.consumable === true || CONSUMABLE_TAX_TYPES.includes(form.tax_type)}
              onToggle={() => {
                if (CONSUMABLE_TAX_TYPES.includes(form.tax_type)) return;
                u({ consumable: form.consumable !== true });
              }}
              testId="product-consumable-toggle"
            />
          </div>

          {/* ── Types / Variants ── */}
          <div className="vp-section-divider">Types</div>
          <p style={{ fontSize: 12, color: "var(--ink-light)", margin: "0 0 12px", lineHeight: 1.5 }}>
            One product, multiple types. Each type can adjust the base price up or down and track its own stock.
          </p>
          <VariantsEditor
            variants={form.variants || []}
            onChange={variants => u({ variants })}
            basePrice={form.price}
            onVariantImageUpload={handleVariantImageUpload}
            isPhysical={form.type === "physical"}
            inheritWeight={form.shipping?.weight_oz || ""}
            inheritDims={form.shipping?.dimensions || {}}
          />

          {/* ── Physical-specific ── */}
          {form.type === "physical" && (
            <>
              <div className="vp-section-divider">Inventory & Shipping</div>
              {hasTypes ? (
                <div style={{
                  background: "var(--linen-dark)", border: "1px solid var(--border)",
                  borderRadius: 10, padding: "12px 14px", marginBottom: 12,
                }} data-testid="inventory-managed-by-types">
                  <div className="vp-toggle-label">
                    Stock: {typeStockTotal} across {namedTypes.length} type{namedTypes.length === 1 ? "" : "s"}
                  </div>
                  <div className="vp-toggle-desc">
                    Managed per type above. Each type keeps its own count, and a sale reduces that
                    type only.
                  </div>
                </div>
              ) : (
                <>
                  <div className="vp-toggle-row">
                    <div>
                      <div className="vp-toggle-label">Track inventory</div>
                      <div className="vp-toggle-desc">Enable to manage stock quantity</div>
                    </div>
                    <Toggle on={form.inventory?.track} onToggle={() => u({ inventory: { ...form.inventory, track: !form.inventory?.track } })} />
                  </div>
                  {form.inventory?.track && (
                    <div className="vp-row" style={{ marginTop: 12 }}>
                      <div className="vp-field">
                        <label className="vp-label">Stock qty</label>
                        <input className="vp-input" type="number" min="0" value={form.inventory?.quantity || ""} onChange={e => u({ inventory: { ...form.inventory, quantity: parseInt(e.target.value) || 0 } })} data-testid="product-stock-qty" />
                      </div>
                      <div className="vp-field">
                        <label className="vp-label">SKU</label>
                        <input className="vp-input" placeholder="e.g. SEL-BOWL-001" value={form.inventory?.sku || ""} onChange={e => u({ inventory: { ...form.inventory, sku: e.target.value } })} />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="vp-row" style={{ marginTop: 12 }}>
                <div className="vp-field">
                  <label className="vp-label">
                    {hasTypes ? "Default weight (oz)" : "Weight (oz)"}{" "}
                    <span style={{ fontWeight: 400, color: "#e85d4a", fontSize: 11 }}>Essential for shipping</span>
                  </label>
                  <input className="vp-input vp-input-sm" style={{ padding: "11px 10px" }} type="number" min="0" step="0.1" placeholder="0.0" value={form.shipping?.weight_oz || ""} onChange={e => u({ shipping: { ...form.shipping, weight_oz: e.target.value } })} data-testid="product-weight" />
                </div>
                <div className="vp-field">
                  <label className="vp-label">{hasTypes ? "Default dimensions (in)" : "Dimensions (in)"}</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                    <input className="vp-input vp-input-sm" style={{ textAlign: "center" }} type="number" min="0" step="0.1" placeholder="L" value={form.shipping?.dimensions?.l || ""} onChange={e => u({ shipping: { ...form.shipping, dimensions: { ...form.shipping?.dimensions, l: e.target.value } } })} data-testid="product-dim-l" />
                    <input className="vp-input vp-input-sm" style={{ textAlign: "center" }} type="number" min="0" step="0.1" placeholder="W" value={form.shipping?.dimensions?.w || ""} onChange={e => u({ shipping: { ...form.shipping, dimensions: { ...form.shipping?.dimensions, w: e.target.value } } })} data-testid="product-dim-w" />
                    <input className="vp-input vp-input-sm" style={{ textAlign: "center" }} type="number" min="0" step="0.1" placeholder="H" value={form.shipping?.dimensions?.h || ""} onChange={e => u({ shipping: { ...form.shipping, dimensions: { ...form.shipping?.dimensions, h: e.target.value } } })} data-testid="product-dim-h" />
                  </div>
                </div>
              </div>
              {hasTypes && (
                <p className="vp-hint" style={{ marginTop: 6 }}>
                  Used for any type that leaves its own weight or size blank — so identical items
                  need it entered once, and varying ones can differ per type.
                </p>
              )}

              <div className="vp-toggle-row" style={{ marginTop: 14 }}>
                <div>
                  <div className="vp-toggle-label">Free shipping on this item</div>
                  <div className="vp-toggle-desc">
                    Buyers pay no postage for this one. You still buy the label, so price it in.
                  </div>
                </div>
                <Toggle
                  on={form.free_shipping === true}
                  onToggle={() => u({ free_shipping: form.free_shipping !== true })}
                  testId="product-free-shipping-toggle"
                />
              </div>

              <div className="vp-section-divider">Made to order</div>
              <div className="vp-toggle-row">
                <div>
                  <div className="vp-toggle-label">Made to order</div>
                  <div className="vp-toggle-desc">
                    You make it once the order lands. Nothing is counted as stock.
                  </div>
                </div>
                <Toggle
                  on={form.made_to_order === true}
                  onToggle={() => u({ made_to_order: form.made_to_order !== true,
                                      print_on_demand: false })}
                  testId="product-made-to-order-toggle"
                />
              </div>
              <div className="vp-toggle-row" style={{ marginTop: 4 }}>
                <div>
                  <div className="vp-toggle-label">Print on demand</div>
                  <div className="vp-toggle-desc">
                    Printed and posted by your fulfiller when the order comes in.
                  </div>
                </div>
                <Toggle
                  on={form.print_on_demand === true}
                  onToggle={() => u({ print_on_demand: form.print_on_demand !== true,
                                      made_to_order: false })}
                  testId="product-print-on-demand-toggle"
                />
              </div>
              {(form.made_to_order || form.print_on_demand) && (
                <div className="vp-row" style={{ marginTop: 12 }}>
                  <div className="vp-field">
                    <label className="vp-label">How long until it ships</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input className="vp-input" type="number" min="1" placeholder="2"
                        style={{ width: 64, textAlign: "center" }}
                        value={form.lead_time_min ?? ""}
                        onChange={e => u({ lead_time_min: e.target.value })}
                        data-testid="product-lead-min" />
                      <span style={{ color: "var(--ink-light)", fontSize: 13 }}>to</span>
                      <input className="vp-input" type="number" min="1" placeholder="3"
                        style={{ width: 64, textAlign: "center" }}
                        value={form.lead_time_max ?? ""}
                        onChange={e => u({ lead_time_max: e.target.value })}
                        data-testid="product-lead-max" />
                      <select className="vp-select" style={{ width: 110 }}
                        value={form.lead_time_unit || "weeks"}
                        onChange={e => u({ lead_time_unit: e.target.value })}
                        data-testid="product-lead-unit">
                        <option value="days">days</option>
                        <option value="weeks">weeks</option>
                      </select>
                    </div>
                    <p className="vp-hint" style={{ marginTop: 6 }}>
                      Buyers see “Made to order — ships in {form.lead_time_min || 2}
                      {(form.lead_time_max && form.lead_time_max !== form.lead_time_min)
                        ? `-${form.lead_time_max}` : ""} {form.lead_time_unit || "weeks"}”
                      on the product and again in their basket.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Digital-specific ── */}
          {form.type === "digital" && (
            <>
              <div className="vp-section-divider">Digital Asset</div>
              <div className="vp-field">
                <label className="vp-label">File type</label>
                <select className="vp-select" value={form.digital_asset?.file_type || "pdf"} onChange={e => u({ digital_asset: { ...form.digital_asset, file_type: e.target.value } })}>
                  <option value="pdf">PDF</option>
                  <option value="audio">Audio (MP3/WAV)</option>
                  <option value="video">Video (MP4)</option>
                  <option value="zip">ZIP Archive</option>
                  <option value="epub">ePub</option>
                </select>
              </div>
              <div className="vp-field">
                <label className="vp-label">File URL</label>
                <input className="vp-input" placeholder="https://storage.your-app.com/files/..." value={form.digital_asset?.file_url || ""} onChange={e => u({ digital_asset: { ...form.digital_asset, file_url: e.target.value } })} />
              </div>
              <div className="vp-field">
                <label className="vp-label">Download limit <span style={{ fontWeight: 400, color: "var(--ink-light)" }}>(leave blank = unlimited)</span></label>
                <input className="vp-input" type="number" min="1" placeholder="e.g. 3" value={form.digital_asset?.download_limit || ""} onChange={e => u({ digital_asset: { ...form.digital_asset, download_limit: e.target.value ? parseInt(e.target.value) : null } })} />
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="vp-drawer-footer">
          <button className="vp-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="vp-btn-publish-draft"
            onClick={() => save(false)}
            disabled={saving}
          >
            Save draft
          </button>
          <button
            className="vp-btn-primary"
            onClick={() => save(true)}
            disabled={saving}
          >
            {saving ? <><Loader size={14} /> Saving...</> : form.status === "active" ? "Save changes" : "Publish"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorProducts() {
  const [products, setProducts] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState(3);
  const [drawerProduct, setDrawerProduct] = useState(undefined);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [vendorSlug, setVendorSlug] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const PRODUCTS_PER_PAGE = 25;

  useEffect(() => {
    apiFetch("/vendor/products").then(setProducts).catch(() => {});
    apiFetch("/vendor/me").then(v => {
      setVendorSlug(v?.store_slug);
      if (v?.low_stock_threshold != null) setLowStockThreshold(v.low_stock_threshold);
    }).catch(() => {});
    apiFetch("/marketplace/categories").then(setCategories).catch(() => {});
  }, []);

  const activeProducts = products.filter(p => p.status !== "archived");
  const archivedProducts = products.filter(p => p.status === "archived");

  // "Low" counts a product whose own count, or ANY of its types, is at or below the
  // threshold — a product sitting on 60 with one size down to 1 is the case that matters.
  const isLow = (p) => {
    if (p.type !== "physical") return false;
    if (p.made_to_order || p.print_on_demand) return false;
    const types = (p.variants || []).filter(v => (v.name || "").trim());
    if (types.length) {
      return types.some(v => (parseInt(v.stock, 10) || 0) <= lowStockThreshold);
    }
    if (!p.inventory?.track) return false;
    return (parseInt(p.inventory.quantity, 10) || 0) <= lowStockThreshold;
  };

  const lowStockCount = activeProducts.filter(isLow).length;

  const filtered = activeProducts.filter(p =>
    (typeFilter === "all" ? TYPE_CONFIG[p.type] : p.type === typeFilter)
    && (!lowStockOnly || isLow(p))
  );

  const totalPages = Math.ceil(filtered.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filtered.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  // Reset to page 1 when filter changes
  useEffect(() => { setCurrentPage(1); }, [typeFilter, lowStockOnly]);

  const counts = Object.fromEntries(
    Object.keys(TYPE_CONFIG).map(t => [t, activeProducts.filter(p => p.type === t).length])
  );

  const saveProduct = (p) => {
    setProducts(prev => {
      const idx = prev.findIndex(x => x.id === p.id);
      return idx >= 0 ? prev.map(x => x.id === p.id ? p : x) : [p, ...prev];
    });
    setDrawerProduct(undefined);
  };

  const toggleStatus = async (id) => {
    const p = products.find(x => x.id === id);
    if (!p) return;
    const newStatus = p.status === "active" ? "draft" : "active";
    try {
      await apiFetch(`/vendor/products/${id}`, {
        method: "PATCH", body: JSON.stringify({ status: newStatus })
      });
      setProducts(prev => prev.map(x => x.id === id ? { ...x, status: newStatus } : x));
    } catch (e) { console.error(e); }
  };

  const archiveProduct = async (id) => {
    try {
      await apiFetch(`/vendor/products/${id}`, { method: "DELETE" });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, status: "archived" } : p));
    } catch (e) { console.error(e); }
    setConfirmArchive(null);
  };

  const restoreProduct = async (id) => {
    try {
      const updated = await apiFetch(`/vendor/products/${id}/restore`, { method: "POST" });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, status: "draft" } : p));
    } catch (e) { console.error(e); }
  };

  const deleteProductPermanently = async (id) => {
    try {
      await apiFetch(`/vendor/products/${id}/permanent`, { method: "DELETE" });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (e) { console.error(e); }
    setConfirmDelete(null);
  };

  const deleteAllArchived = async () => {
    try {
      await apiFetch("/vendor/products/archived/all", { method: "DELETE" });
      setProducts(prev => prev.filter(p => p.status !== "archived"));
      setShowArchive(false);
    } catch (e) { console.error(e); }
    setConfirmDeleteAll(false);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="vp-root">

        {/* Header */}
        <div className="vp-header">
          <div>
            <h1>Products</h1>
            <p>{products.filter(p => p.status === "active").length} active · {products.filter(p => p.status === "draft").length} draft{archivedProducts.length > 0 ? ` · ${archivedProducts.length} archived` : ""}</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {archivedProducts.length > 0 && (
              <button
                className="vp-btn-new"
                style={{ background: showArchive ? "var(--ink-mid)" : "var(--ink-light)", fontSize: 13 }}
                onClick={() => setShowArchive(!showArchive)}
                data-testid="toggle-archive-btn"
              >
                <Archive size={15} /> Archive ({archivedProducts.length})
              </button>
            )}
            {vendorSlug && (
              <button
                className="vp-btn-new"
                style={{ background: "var(--copper)", fontSize: 13 }}
                onClick={() => window.open(`/dashboard/marketplace/store/${vendorSlug}`, '_blank')}
                data-testid="preview-store-btn"
              >
                <ExternalLink size={15} /> Preview Store
              </button>
            )}
            <button
              className="vp-btn-new"
              style={{ background: "var(--forest-mid)", fontSize: 13 }}
              onClick={() => setShowImport(true)}
              data-testid="import-products-btn"
            >
              <Globe size={15} /> Import
            </button>
            <button className="vp-btn-new" onClick={() => setDrawerProduct(null)} data-testid="add-product-btn">
              <Plus size={16} /> Add Product
            </button>
          </div>
        </div>

        {/* Type tabs */}
        <div className="vp-type-tabs">
          <button className={`vp-type-tab ${typeFilter === "all" ? "active" : ""}`} onClick={() => setTypeFilter("all")}>
            All <span className="vp-type-tab-count">{products.length}</span>
          </button>
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={key}
                className={`vp-type-tab ${typeFilter === key ? "active" : ""}`}
                onClick={() => setTypeFilter(key)}
              >
                <Icon size={13} /> {cfg.label}
                <span className="vp-type-tab-count">{counts[key]}</span>
              </button>
            );
          })}
          {lowStockCount > 0 && (
            <button
              className={`vp-type-tab ${lowStockOnly ? "active" : ""}`}
              onClick={() => setLowStockOnly(v => !v)}
              style={lowStockOnly ? undefined : { borderColor: "#e6c9a3", color: "#a15c12" }}
              data-testid="low-stock-filter"
            >
              <AlertTriangle size={13} /> Low stock
              <span className="vp-type-tab-count">{lowStockCount}</span>
            </button>
          )}
        </div>

        {/* Collections — their own category words, each with a cover */}
        <VendorCollectionCovers />

        {/* Grid */}
        <div className="vp-grid">
          {filtered.length === 0 ? (
            <div className="vp-empty">
              <div className="vp-empty-icon"><Package size={28} /></div>
              <p style={{ fontFamily: "'Spectral', serif", fontSize: 18, color: "var(--ink)", margin: "0 0 6px" }}>No products yet</p>
              <p style={{ fontSize: 13, color: "var(--ink-light)", margin: "0 0 20px" }}>Add your first product to get started.</p>
              <button className="vp-btn-new" onClick={() => setDrawerProduct(null)}>
                <Plus size={15} /> Add Product
              </button>
            </div>
          ) : (
            paginatedProducts.map(p => {
              const tc = TYPE_CONFIG[p.type];
              if (!tc) return null;
              const Icon = tc.icon;
              return (
                <div key={p.id} className="vp-card">
                  <div className="vp-card-img">
                    {p.images?.length > 0 ? (
                      <img src={p.images[0]} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <Icon size={32} color={tc.color} style={{ opacity: 0.4 }} />
                    )}
                    <span className={`vp-card-type-badge vp-badge-${p.type}`}>{tc.label}</span>
                    <span className={`vp-card-status-dot ${p.status}`} title={p.status} />
                  </div>
                  <div className="vp-card-body">
                    <p className="vp-card-name">{p.title}</p>
                    <div className="vp-card-meta">
                      <span className="vp-card-price">${p.price}</span>
                      {p.category && <span>· {p.category}</span>}
                      {p.status === "draft" && <span style={{ color: "var(--copper)", fontWeight: 700 }}>Draft</span>}
                    </div>
                    {p.type === "physical" && (
                      <ProductStock
                        product={p}
                        threshold={lowStockThreshold}
                        onChange={updated => setProducts(prev =>
                          prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))}
                      />
                    )}
                    <div className="vp-card-actions">
                      <button className="vp-card-btn" onClick={() => setDrawerProduct(p)}>
                        <Edit2 size={12} /> Edit
                      </button>
                      <button
                        className={`vp-card-btn ${p.status === "active" ? "unpublish" : "publish"}`}
                        onClick={() => toggleStatus(p.id)}
                      >
                        {p.status === "active"
                          ? <><EyeOff size={12} /> Unpublish</>
                          : <><Eye size={12} /> Publish</>
                        }
                      </button>
                      <button className="vp-card-btn archive" onClick={() => setConfirmArchive(p)}>
                        <Archive size={12} /> Archive
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, padding: '20px 0',
          }} data-testid="products-pagination">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid var(--border)',
                background: currentPage === 1 ? 'var(--linen)' : 'var(--surface)',
                color: currentPage === 1 ? 'var(--ink-light)' : 'var(--ink)',
                cursor: currentPage === 1 ? 'default' : 'pointer',
                fontSize: 13, fontWeight: 600,
              }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                data-testid={`products-page-${p}`}
                style={{
                  width: 36, height: 36, borderRadius: 8,
                  border: currentPage === p ? '1.5px solid var(--forest)' : '1px solid var(--border)',
                  background: currentPage === p ? 'var(--forest)' : 'var(--surface)',
                  color: currentPage === p ? '#fff' : 'var(--ink-mid)',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid var(--border)',
                background: currentPage === totalPages ? 'var(--linen)' : 'var(--surface)',
                color: currentPage === totalPages ? 'var(--ink-light)' : 'var(--ink)',
                cursor: currentPage === totalPages ? 'default' : 'pointer',
                fontSize: 13, fontWeight: 600,
              }}
            >
              Next
            </button>
            <span style={{ fontSize: 12, color: 'var(--ink-light)', marginLeft: 8 }}>
              {filtered.length} product{filtered.length !== 1 ? 's' : ''} · Page {currentPage} of {totalPages}
            </span>
          </div>
        )}

        {/* Archive Section */}
        {showArchive && archivedProducts.length > 0 && (
          <div style={{
            marginTop: 32, padding: "24px 20px", borderRadius: 16,
            background: "rgba(0,0,0,0.03)", border: "1px dashed var(--ink-light)",
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 16, flexWrap: "wrap", gap: 12,
            }}>
              <div>
                <h3 style={{ fontFamily: "'Spectral', serif", fontSize: 18, margin: 0, color: "var(--ink)" }}>
                  <Archive size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Archive ({archivedProducts.length} items)
                </h3>
                <p style={{ fontSize: 12, color: "var(--ink-light)", margin: "4px 0 0" }}>
                  Archived products are hidden from your store. Restore or delete them permanently.
                </p>
              </div>
              <button
                style={{
                  padding: "8px 16px", borderRadius: 10, border: "none",
                  background: "var(--red)", color: "white", cursor: "pointer",
                  fontFamily: "'Nunito Sans', sans-serif", fontSize: 13, fontWeight: 700,
                }}
                onClick={() => setConfirmDeleteAll(true)}
                data-testid="delete-all-archived-btn"
              >
                <Trash2 size={13} style={{ marginRight: 4, verticalAlign: -2 }} /> Delete All
              </button>
            </div>
            <div className="vp-grid">
              {archivedProducts.map(p => {
                const tc = TYPE_CONFIG[p.type] || { icon: Package, color: "#999", label: p.type };
                const Icon = tc.icon;
                return (
                  <div key={p.id} className="vp-card" style={{ opacity: 0.75 }}>
                    <div className="vp-card-img">
                      {p.images?.length > 0 ? (
                        <img src={p.images[0]} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(0.5)" }} />
                      ) : (
                        <Icon size={32} color={tc.color} style={{ opacity: 0.3 }} />
                      )}
                      <span className={`vp-card-type-badge vp-badge-${p.type}`}>{tc.label}</span>
                    </div>
                    <div className="vp-card-body">
                      <p className="vp-card-name">{p.title}</p>
                      <div className="vp-card-meta">
                        <span className="vp-card-price">${p.price}</span>
                        {p.category && <span>· {p.category}</span>}
                      </div>
                      <div className="vp-card-actions">
                        <button className="vp-card-btn publish" onClick={() => restoreProduct(p.id)} data-testid={`restore-${p.id}`}>
                          <Eye size={12} /> Restore
                        </button>
                        <button className="vp-card-btn archive" onClick={() => setConfirmDelete(p)} data-testid={`delete-${p.id}`}>
                          <X size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Product drawer */}
        {drawerProduct !== undefined && (
          <ProductDrawer
            product={drawerProduct}
            onClose={() => setDrawerProduct(undefined)}
            onSave={saveProduct}
            categories={categories}
            onCreateCategory={async (name) => {
              const cat = await apiFetch("/marketplace/categories", {
                method: "POST",
                body: JSON.stringify({ name }),
              });
              setCategories(prev => [...prev, cat].sort((a, b) => a.name.localeCompare(b.name)));
              return cat;
            }}
          />
        )}

        {/* Archive confirm */}
        {confirmArchive && (
          <div className="vp-overlay" onClick={() => setConfirmArchive(null)}>
            <div
              style={{
                position: "fixed", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white", borderRadius: 16, padding: 28,
                maxWidth: 380, width: "calc(100% - 48px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                zIndex: 102,
              }}
              onClick={e => e.stopPropagation()}
            >
              <p style={{ fontFamily: "'Spectral', serif", fontSize: 19, fontWeight: 600, margin: "0 0 8px" }}>
                Archive "{confirmArchive.title}"?
              </p>
              <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.55, margin: "0 0 20px" }}>
                This product will be hidden from your store and cannot be purchased. You can restore it later.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="vp-btn-secondary" onClick={() => setConfirmArchive(null)}>Cancel</button>
                <button
                  style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "var(--red)", color: "white", fontFamily: "'Nunito Sans', sans-serif", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => archiveProduct(confirmArchive.id)}
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Product Import Modal */}
        {showImport && (
          <ProductImportModal
            onClose={() => setShowImport(false)}
            onImportComplete={() => {
              setShowImport(false);
              apiFetch("/vendor/products").then(setProducts).catch(() => {});
            }}
          />
        )}

        {/* Permanent delete confirm */}
        {confirmDelete && (
          <div className="vp-overlay" onClick={() => setConfirmDelete(null)}>
            <div
              style={{
                position: "fixed", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white", borderRadius: 16, padding: 28,
                maxWidth: 380, width: "calc(100% - 48px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                zIndex: 102,
              }}
              onClick={e => e.stopPropagation()}
            >
              <p style={{ fontFamily: "'Spectral', serif", fontSize: 19, fontWeight: 600, margin: "0 0 8px", color: "var(--red)" }}>
                Permanently delete "{confirmDelete.title}"?
              </p>
              <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.55, margin: "0 0 20px" }}>
                This action cannot be undone. The product and all its data will be permanently removed.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="vp-btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button
                  style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "var(--red)", color: "white", fontFamily: "'Nunito Sans', sans-serif", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => deleteProductPermanently(confirmDelete.id)}
                  data-testid="confirm-permanent-delete"
                >
                  Delete Forever
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete all archived confirm */}
        {confirmDeleteAll && (
          <div className="vp-overlay" onClick={() => setConfirmDeleteAll(false)}>
            <div
              style={{
                position: "fixed", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white", borderRadius: 16, padding: 28,
                maxWidth: 420, width: "calc(100% - 48px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                zIndex: 102,
              }}
              onClick={e => e.stopPropagation()}
            >
              <p style={{ fontFamily: "'Spectral', serif", fontSize: 19, fontWeight: 600, margin: "0 0 8px", color: "var(--red)" }}>
                Delete all {archivedProducts.length} archived products?
              </p>
              <p style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.55, margin: "0 0 20px" }}>
                This will permanently remove <strong>all archived products</strong> from your store. This action cannot be undone.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="vp-btn-secondary" onClick={() => setConfirmDeleteAll(false)}>Cancel</button>
                <button
                  style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "var(--red)", color: "white", fontFamily: "'Nunito Sans', sans-serif", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  onClick={deleteAllArchived}
                  data-testid="confirm-delete-all-archived"
                >
                  Delete All Forever
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
